import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from "react-router-dom";
import Footer from './Footer.js';
import LoadingAnimation from './LoadingAnimation.js';
import * as utils from '../common/Utils.js'
import { useConfig } from '../contexts/ConfigContext.js';

import { collection, setDoc, doc, Timestamp } from "firebase/firestore";
import { db } from '../firebase';

const NewPlayer = () => {
  const navigate = useNavigate();
  const config = useConfig();
  const CURRENT_SEASON = config.currentSeason;
  const [dataReady, setDataReady] = useState(false);
  const [isFormSubmitting, setIsFormSubmitting] = useState(false);
  // Ref (not state) so a rapid second tap is blocked before React re-renders
  const isSavingRef = useRef(false);

  useEffect(() => {
    if (!config.configLoaded) return;
    setDataReady(true);
  }, [config.configLoaded, CURRENT_SEASON])

  const uniqueId = () => {
    const dateString = Date.now().toString(36);
    const randomness = Math.random().toString(36).substr(2);
    return dateString + randomness;
  }

  // IDs are created once per visit to the form, so a repeat submit (double tap,
  // retry after a slow network) writes to the same documents instead of
  // creating duplicate players/signups. The signup ID is a normal Firestore
  // auto-ID, generated client-side without a write.
  const [playerID] = useState(() => uniqueId());
  const [signupID] = useState(() => doc(collection(db, "signups")).id);

  const savePlayer = async () => {
    if (isSavingRef.current) return;
    isSavingRef.current = true;
    setIsFormSubmitting(true);

    const birthdayValue = document.getElementById('inputBirthday').value;
    let birthday = { month: '', day: '', year: '' };

    if (birthdayValue) {
      const dateObj = new Date(birthdayValue);
      birthday = {
        month: dateObj.getMonth() + 1, // months are 0-indexed
        day: dateObj.getDate(),
        year: dateObj.getFullYear()
      };
    }

    let player = {
      age: '',
      birthday: birthday,
      email: document.getElementById('inputEmail').value,
      height: {
        feet: document.getElementById('inputHeightFeet').value,
        inches: document.getElementById('inputHeightInches').value
      },
      jersey_number: '',
      jersey_size: document.getElementById('inputJerseySize').value,
      first_name: document.getElementById('inputFirstName').value,
      last_name: document.getElementById('inputLastName').value,
      paid_fee: '',
      player_id: playerID,
      phone: document.getElementById('inputPhone').value,
      team_id: ''
    }

    if (player.first_name === '') {
      setIsFormSubmitting(false);
      isSavingRef.current = false;
      return
    }
    if (player.last_name === '') {
      setIsFormSubmitting(false);
      isSavingRef.current = false;
      return
    }
    if (player.email === '') {
      setIsFormSubmitting(false);
      isSavingRef.current = false;
      return
    }

    await setDoc(doc(db, "players", playerID), {
      email: document.getElementById('inputEmail').value,
      firstName: document.getElementById('inputFirstName').value,
      height: {
        inches: document.getElementById('inputHeightInches').value,
        feet: document.getElementById('inputHeightFeet').value
      },
      id: playerID,
      imageURL: '',
      jerseyNumber: '',
      jerseySize: document.getElementById('inputJerseySize').value,
      lastName: document.getElementById('inputLastName').value,
      phone: document.getElementById('inputPhone').value,
      age: '',
      birthday: birthday
    });

    // set blank playerView
    let blankStats = {
      assists: 0,
      blocks: 0,
      points: 0,
      rebounds: 0,
      steals: 0,
      gamesPlayed: 0,
      teams: []
    }
    await setDoc(doc(db, "playerViews", playerID), {
      email: player.email,
      firstName: player.first_name,
      lastName: player.last_name,
      imageURL: '',
      seasonAverages: {
        [CURRENT_SEASON]: blankStats,
        all: blankStats
      },
      seasonStats: {
        [CURRENT_SEASON]: []
      }
    }, { merge: true });

    // set signup
    await setDoc(doc(db, "signups", signupID), {
      created_at: Timestamp.fromDate(new Date()),
      player_id: playerID,
      status: 'new',
      season: CURRENT_SEASON,
      paid: false,
      first_name: document.getElementById('inputFirstName').value,
      last_name: document.getElementById('inputLastName').value
    });

    navigate(`/signup/all/${playerID}`)
  }

  function displayNewPlayerForm () {
    return (
      <form className="signup-form">
        <div className="signup-form-row">
          <div>
            <label className="signup-label" htmlFor="inputFirstName">First Name</label>
            <input type="text" className="signup-input" id="inputFirstName" required disabled={isFormSubmitting}></input>
          </div>
          <div>
            <label className="signup-label" htmlFor="inputLastName">Last Name</label>
            <input type="text" className="signup-input" id="inputLastName" required disabled={isFormSubmitting}></input>
          </div>
        </div>
        <div>
          <label className="signup-label" htmlFor="inputEmail">Email</label>
          <input type="email" className="signup-input" id="inputEmail" required disabled={isFormSubmitting}></input>
        </div>
        <div>
          <label className="signup-label" htmlFor="inputPhone">Phone Number</label>
          <input type="tel" className="signup-input" id="inputPhone" required disabled={isFormSubmitting}></input>
        </div>
        <div className="signup-form-row">
          <div>
            <label className="signup-label" htmlFor="inputHeightFeet">Height (ft)</label>
            <select className="signup-input" id="inputHeightFeet" defaultValue="6" disabled={isFormSubmitting}>
              <option value="5">5</option>
              <option value="6">6</option>
              <option value="7">7</option>
            </select>
          </div>
          <div>
            <label className="signup-label" htmlFor="inputHeightInches">Height (in)</label>
            <select className="signup-input" id="inputHeightInches" defaultValue="0" disabled={isFormSubmitting}>
              {[...Array(12).keys()].map(i => <option key={i} value={String(i)}>{i}</option>)}
            </select>
          </div>
        </div>
        <div className="signup-form-row">
          <div>
            <label className="signup-label" htmlFor="inputBirthday">Birthday</label>
            <input type="date" className="signup-input" id="inputBirthday" disabled={isFormSubmitting} />
          </div>
          <div>
            <label className="signup-label" htmlFor="inputJerseySize">Jersey Size</label>
            <select className="signup-input" id="inputJerseySize" defaultValue="L" disabled={isFormSubmitting}>
              <option value="S">S</option>
              <option value="M">M</option>
              <option value="L">L</option>
              <option value="XL">XL</option>
              <option value="XXL">XXL</option>
            </select>
          </div>
        </div>

        <button className="signup-btn" type="button" onClick={savePlayer} disabled={isFormSubmitting}>
          {isFormSubmitting ? 'Signing Up...' : 'Sign Up ›'}
        </button>
      </form>
    )
  }

  if (dataReady == false) {
    return (
      <div className="HomepageUpdated signup-flow">
        <LoadingAnimation />
      </div>
    )
  }
  else {
    return (
      <div className="HomepageUpdated signup-flow">
        <div className="league-content">
          <div className="league-homepage">

            <nav className="homepage-nav">
              <p className="homepage-nav-label">Season {utils.toRomanNumeral(parseInt(CURRENT_SEASON))} Sign Ups</p>
            </nav>
            <hr className="section-divider" />

            <div className="homepage-section">
              <p className="mini-title">New Player</p>
              <p className="signup-body-text">Already played in the league? <a href="/signup/returning">Sign up as a returning player</a>.</p>
            </div>

            <div className="homepage-section">
              { displayNewPlayerForm() }
            </div>
            <hr className="section-divider" />

          </div>
        </div>

        <Footer/>
      </div>
    );
  }
}

export default NewPlayer;
